import { graphql } from 'app/generated/gql'
import type { Client } from '@urql/core'
import { chunk } from 'lodash'
import { makeObservable, observable, runInAction } from 'mobx'
import {
  SortDirection,
  type Contact as ContactType,
  type ContactListResult,
  type SortInput,
} from 'app/generated/graphql'
import type { ContactJson } from 'app/types/entities'
import { readListPage } from 'app/lib/listApi'
import { notifyError } from 'app/services/notify.client'
import { withoutRemovedMembers } from 'lib/listPlacements'
import { logError } from 'lib/logging'
import { EntityStore } from './EntityStore'

const LIST_QUERY = graphql(`
  query Contacts(
    $search: String
    $pagination: PaginationInput
    $sort: SortInput
    $magicTerms: Boolean
    $fields: [String!]
  ) {
    contacts(search: $search, pagination: $pagination, sort: $sort, magicTerms: $magicTerms, fields: $fields) {
      hits {
        activityCount
        activityTypes
        adClickDate
        addressLine1
        addressLine2
        appointmentStatus
        archived
        becameInactiveDate
        becameLeadDate
        becamePatientDate
        birthDate
        cellPhone
        channel
        city
        closedLostReason
        collectedLtv
        convertedBy {
          id
          name
        }
        createdDate
        email
        fbclid
        firstActivityDate
        firstActivityType
        firstAppointmentDate
        firstAppointmentMadeDate
        firstContactReason
        firstName
        firstTransactionDate
        fvv
        gclid
        homePhone
        hygieneDueDate
        hygieneStatus
        id
        isPerio
        isQualified
        landingPageURL
        lastActivityDate
        lastAppointmentDate
        lastCommActivitySubType
        lastCommActivityType
        lastCommDate
        lastHygieneDate
        lastName
        lastTransactionDate
        lastTreatmentPlanDate
        leadConversionStatus
        leadOwner {
          id
          name
        }
        leadStatus
        lifecycleStage
        locationId
        matchTypes
        name
        nearestAppointmentDate
        neededAction
        needsActionDate
        orgId
        otherPhone
        patientCreatedDate
        patientIds
        patientName
        patientReferral
        patientStatus
        pipelines {
          id
          stage
        }
        platform
        preferredCommunicationMethod
        preferredContact
        preferredName
        productionLtv
        referralURL
        reportedAttribution
        scheduledProduction
        smsMarketingConsent
        state
        tags
        trackableCampaignId
        treatmentStatus
        unbookedTreatmentValue
        updatedDate
        utmCampaign
        utmContent
        utmKeywords
        utmMedium
        utmSource
        version
        workPhone
        workspaceId
        zip
      }
      total
      page
    }
  }
`)

const GET_QUERY = graphql(`
  query Contact($id: ID!) {
    contact(id: $id) {
      activityCount
      activityTypes
      adClickDate
      addressLine1
      addressLine2
      appointmentStatus
      archived
      becameInactiveDate
      becameLeadDate
      becamePatientDate
      birthDate
      cellPhone
      channel
      city
      closedLostReason
      collectedLtv
      convertedBy {
        id
        name
      }
      createdDate
      email
      fbclid
      firstActivityDate
      firstActivityType
      firstAppointmentDate
      firstAppointmentMadeDate
      firstContactReason
      firstName
      firstTransactionDate
      fvv
      gclid
      homePhone
      hygieneDueDate
      hygieneStatus
      id
      isPerio
      isQualified
      landingPageURL
      lastActivityDate
      lastAppointmentDate
      lastCommActivitySubType
      lastCommActivityType
      lastCommDate
      lastHygieneDate
      lastName
      lastTransactionDate
      lastTreatmentPlanDate
      leadConversionStatus
      leadOwner {
        id
        name
      }
      leadStatus
      lifecycleStage
      locationId
      matchTypes
      name
      nearestAppointmentDate
      neededAction
      needsActionDate
      orgId
      otherPhone
      patientCreatedDate
      patientIds
      patientName
      patientReferral
      patientStatus
      pipelines {
        id
        stage
      }
      platform
      preferredCommunicationMethod
      preferredContact
      preferredName
      productionLtv
      referralURL
      reportedAttribution
      scheduledProduction
      smsMarketingConsent
      state
      tags
      trackableCampaignId
      treatmentStatus
      unbookedTreatmentValue
      updatedDate
      utmCampaign
      utmContent
      utmKeywords
      utmMedium
      utmSource
      version
      workPhone
      workspaceId
      zip
    }
  }
`)

const CREATE_MUTATION = graphql(`
  mutation CreateContact($input: CreateContactInput!) {
    createContact(input: $input) {
      activityCount
      activityTypes
      addressLine1
      addressLine2
      appointmentStatus
      archived
      becameInactiveDate
      becameLeadDate
      becamePatientDate
      birthDate
      cellPhone
      channel
      city
      closedLostReason
      collectedLtv
      createdDate
      email
      firstActivityDate
      firstActivityType
      firstAppointmentDate
      firstAppointmentMadeDate
      firstContactReason
      firstTransactionDate
      fvv
      homePhone
      id
      hygieneDueDate
      hygieneStatus
      treatmentStatus
      isPerio
      isQualified
      lastActivityDate
      lastAppointmentDate
      lastCommDate
      lastCommActivityType
      lastCommActivitySubType
      lastHygieneDate
      lastTransactionDate
      leadOwner {
        id
        name
      }
      leadStatus
      lifecycleStage
      locationId
      matchTypes
      name
      nearestAppointmentDate
      neededAction
      needsActionDate
      orgId
      otherPhone
      patientCreatedDate
      patientIds
      patientName
      patientReferral
      patientStatus
      pipelines {
        id
        stage
      }
      platform
      preferredCommunicationMethod
      preferredContact
      preferredName
      productionLtv
      reportedAttribution
      scheduledProduction
      smsMarketingConsent
      state
      tags
      trackableCampaignId
      updatedDate
      utmCampaign
      utmContent
      utmKeywords
      utmMedium
      utmSource
      version
      workPhone
      workspaceId
      zip
    }
  }
`)

const UPDATE_MUTATION = graphql(`
  mutation UpdateContactStore($id: ID!, $input: UpdateContactInput!) {
    updateContact(id: $id, input: $input) {
      activityCount
      activityTypes
      addressLine1
      addressLine2
      appointmentStatus
      archived
      becameInactiveDate
      becameLeadDate
      becamePatientDate
      birthDate
      cellPhone
      channel
      city
      closedLostReason
      collectedLtv
      createdDate
      email
      firstActivityDate
      firstActivityType
      firstAppointmentDate
      firstAppointmentMadeDate
      firstContactReason
      firstTransactionDate
      fvv
      homePhone
      id
      hygieneDueDate
      hygieneStatus
      treatmentStatus
      isPerio
      isQualified
      lastActivityDate
      lastAppointmentDate
      lastCommDate
      lastCommActivityType
      lastCommActivitySubType
      lastHygieneDate
      lastTransactionDate
      leadOwner {
        id
        name
      }
      leadStatus
      lifecycleStage
      locationId
      matchTypes
      name
      nearestAppointmentDate
      neededAction
      needsActionDate
      orgId
      otherPhone
      patientCreatedDate
      patientIds
      patientName
      patientReferral
      patientStatus
      pipelines {
        id
        stage
      }
      platform
      preferredCommunicationMethod
      preferredContact
      preferredName
      productionLtv
      reportedAttribution
      scheduledProduction
      smsMarketingConsent
      state
      tags
      trackableCampaignId
      updatedDate
      utmCampaign
      utmContent
      utmKeywords
      utmMedium
      utmSource
      version
      workPhone
      workspaceId
      zip
    }
  }
`)

const MERGE_CONTACTS = graphql(`
  mutation MergeContacts($leftId: ID!, $rightId: ID!) {
    mergeContacts(leftId: $leftId, rightId: $rightId) {
      id
    }
  }
`)

const RECALCULATE_CONTACT = graphql(`
  mutation RecalculateContact($id: ID!) {
    recalculateContact(id: $id) {
      activityCount
      activityTypes
      addressLine1
      addressLine2
      appointmentStatus
      archived
      becameInactiveDate
      becameLeadDate
      becamePatientDate
      birthDate
      cellPhone
      channel
      city
      closedLostReason
      collectedLtv
      createdDate
      email
      firstActivityDate
      firstActivityType
      firstAppointmentDate
      firstAppointmentMadeDate
      firstContactReason
      firstTransactionDate
      fvv
      homePhone
      id
      hygieneDueDate
      hygieneStatus
      treatmentStatus
      isPerio
      isQualified
      lastActivityDate
      lastAppointmentDate
      lastCommDate
      lastCommActivityType
      lastCommActivitySubType
      lastHygieneDate
      lastTransactionDate
      leadOwner {
        id
        name
      }
      leadStatus
      lifecycleStage
      locationId
      matchTypes
      name
      nearestAppointmentDate
      neededAction
      needsActionDate
      orgId
      otherPhone
      patientCreatedDate
      patientIds
      patientName
      patientReferral
      patientStatus
      pipelines {
        id
        stage
      }
      platform
      preferredCommunicationMethod
      preferredContact
      preferredName
      productionLtv
      reportedAttribution
      scheduledProduction
      smsMarketingConsent
      state
      tags
      trackableCampaignId
      updatedDate
      utmCampaign
      utmContent
      utmKeywords
      utmMedium
      utmSource
      version
      workPhone
      workspaceId
      zip
    }
  }
`)

const ADD_TAGS_MUTATION = graphql(`
  mutation AddTags($id: ID!, $tags: [String!]!) {
    addTags(id: $id, tags: $tags) {
      activityCount
      activityTypes
      addressLine1
      addressLine2
      appointmentStatus
      archived
      becameInactiveDate
      becameLeadDate
      becamePatientDate
      birthDate
      cellPhone
      channel
      city
      closedLostReason
      collectedLtv
      createdDate
      email
      firstActivityDate
      firstActivityType
      firstAppointmentDate
      firstAppointmentMadeDate
      firstContactReason
      firstTransactionDate
      fvv
      homePhone
      id
      hygieneDueDate
      hygieneStatus
      treatmentStatus
      isPerio
      isQualified
      lastActivityDate
      lastAppointmentDate
      lastCommDate
      lastCommActivityType
      lastCommActivitySubType
      lastHygieneDate
      lastTransactionDate
      leadOwner {
        id
        name
      }
      leadStatus
      lifecycleStage
      locationId
      matchTypes
      name
      nearestAppointmentDate
      neededAction
      needsActionDate
      orgId
      otherPhone
      patientCreatedDate
      patientIds
      patientName
      patientReferral
      patientStatus
      pipelines {
        id
        stage
      }
      platform
      preferredCommunicationMethod
      preferredContact
      preferredName
      productionLtv
      reportedAttribution
      scheduledProduction
      smsMarketingConsent
      state
      tags
      trackableCampaignId
      updatedDate
      utmCampaign
      utmContent
      utmKeywords
      utmMedium
      utmSource
      version
      workPhone
      workspaceId
      zip
    }
  }
`)

const ADD_TO_PIPELINE_MUTATION = graphql(`
  mutation AddToPipeline($id: ID!, $pipeline: ContactPipelineInput!) {
    addToPipeline(id: $id, pipeline: $pipeline) {
      activityCount
      activityTypes
      addressLine1
      addressLine2
      appointmentStatus
      archived
      becameInactiveDate
      becameLeadDate
      becamePatientDate
      birthDate
      cellPhone
      channel
      city
      closedLostReason
      collectedLtv
      createdDate
      email
      firstActivityDate
      firstActivityType
      firstAppointmentDate
      firstAppointmentMadeDate
      firstContactReason
      firstTransactionDate
      fvv
      homePhone
      id
      hygieneDueDate
      hygieneStatus
      treatmentStatus
      isPerio
      isQualified
      lastActivityDate
      lastAppointmentDate
      lastCommDate
      lastCommActivityType
      lastCommActivitySubType
      lastHygieneDate
      lastTransactionDate
      leadOwner {
        id
        name
      }
      leadStatus
      lifecycleStage
      locationId
      matchTypes
      name
      nearestAppointmentDate
      neededAction
      needsActionDate
      orgId
      otherPhone
      patientCreatedDate
      patientIds
      patientName
      patientReferral
      patientStatus
      pipelines {
        id
        stage
      }
      platform
      preferredCommunicationMethod
      preferredContact
      preferredName
      productionLtv
      reportedAttribution
      scheduledProduction
      smsMarketingConsent
      state
      tags
      trackableCampaignId
      updatedDate
      utmCampaign
      utmContent
      utmKeywords
      utmMedium
      utmSource
      version
      workPhone
      workspaceId
      zip
    }
  }
`)

// Bounds one search clause; a stage being emptied can hold thousands of contacts.
const ID_SEARCH_CHUNK = 500

export class ContactStore extends EntityStore<
  ContactType,
  ContactListResult,
  typeof CREATE_MUTATION,
  typeof UPDATE_MUTATION
> {
  /** Contacts recently added to pipelines, keyed by pipeline ID. Covers ES eventual consistency gap. */
  pendingPipelineContacts = new Map<string, ContactJson[]>()
  /** The stage of each member the last list-member read returned, by contact id. Replaced, never mutated, on every read. */
  memberStages = new Map<string, string | undefined>()
  /**
   * Set while the table shows a list's members, so `fetchNextPage` pages the member read, not the contacts query.
   * `dropped` counts the removed members the reads returned and the table left out: the index still counts them.
   */
  private memberRead:
    | {
        listId: string
        search?: string
        sort?: SortInput
        limit: number
        page: number
        loaded: number
        dropped: number
      }
    | undefined

  constructor(client: Client) {
    super(client, {
      listQuery: LIST_QUERY,
      getQuery: GET_QUERY,
      createMutation: CREATE_MUTATION,
      updateMutation: UPDATE_MUTATION,
      extractList: (data: any) => data.contacts,
      extractOne: (data: any) => data.contact,
      extractMutated: (data: any) => data.updateContact ?? data.createContact,
    })
    // makeObservable (not makeAutoObservable): extends EntityStore (subclass);
    // annotates only this store's own `pendingPipelineContacts` field.
    makeObservable(this, {
      pendingPipelineContacts: observable,
      memberStages: observable.ref,
    })
  }

  async fetchList(...args: Parameters<EntityStore<ContactType, ContactListResult>['fetchList']>) {
    if (this.memberRead) {
      runInAction(() => {
        this.memberRead = undefined
        this.memberStages = new Map()
      })
    }
    return super.fetchList(...args)
  }

  /**
   * Contact picker candidates: the workspace's contacts whose name contains `query`, first page of 20.
   * Goes through `search`, so the hits are the shared instances, the contacts list stays as it is,
   * and a cached result shows at once while the server result revalidates it (`onRevalidate`).
   */
  searchByName(
    { query, workspaceId }: { query?: string; workspaceId?: string },
    opts?: Parameters<ContactStore['search']>[1],
  ) {
    const terms = [workspaceId ? `workspaceId:${workspaceId}` : ``, query ? `name:*${query}*` : ``].filter(Boolean)
    return this.search(
      { search: terms.length ? terms.join(` AND `) : undefined, pagination: { page: 0, limit: 20 } },
      opts,
    )
  }

  /** Contacts by id, as the shared instances. A contact that no longer exists is simply absent. */
  async readByIds(ids: readonly string[]): Promise<ContactType[]> {
    const contacts: ContactType[] = []
    for (const batch of chunk(ids, ID_SEARCH_CHUNK)) {
      const { hits, error } = await this.search({
        search: `id:(${batch.map((id) => `"${id}"`).join(` OR `)})`,
        pagination: { page: 0, limit: batch.length },
      })
      if (error) throw new Error(error.message)
      contacts.push(...hits)
    }
    return contacts
  }

  /**
   * Fill `list` with a list's members, in the List API's member order. Members hold the stage, so it
   * lands in `memberStages`; the contacts come from the contacts query. Shares `listRequestId` with
   * `fetchList` so whichever read started last is the only one that lands.
   */
  async fetchListMembers(args: {
    listId: string
    search?: string
    sort?: { field: string; direction: 'ASC' | 'DESC' }
    pagination: { page: number; limit: number }
  }) {
    const { listId, search, pagination } = args
    // The table speaks 'ASC'/'DESC'; the List API takes the enum. Absent, the List API's own default order applies.
    const sort = args.sort && {
      field: args.sort.field,
      direction: args.sort.direction === 'DESC' ? SortDirection.Desc : SortDirection.Asc,
    }
    await this.readMembers(listId, search, sort, pagination)
  }

  /**
   * Read the members again from the first page, as after a Remove. Patching the loaded pages would leave the next
   * page's offset counting rows the index has since dropped; the tombstones keep the removed rows out meanwhile.
   */
  async refetchListMembers(listId: string) {
    const read = this.memberRead
    if (!read || read.listId !== listId) return
    await this.readMembers(listId, read.search, read.sort, { page: 0, limit: read.limit })
  }

  private async readMembers(
    listId: string,
    search: string | undefined,
    sort: SortInput | undefined,
    pagination: { page: number; limit: number },
  ) {
    runInAction(() => {
      this.listLoading = true
      this.listError = undefined
    })
    const requestId = ++this.listRequestId
    try {
      const { contacts, total, stageByEntity } = await readListPage({ listId, search, sort, ...pagination })
      const { kept, dropped } = withoutRemovedMembers(listId, contacts)
      runInAction(() => {
        if (requestId !== this.listRequestId) return
        this.listLoading = false
        this.list = { hits: kept as unknown as ContactType[], total: total - dropped, page: pagination.page }
        this.memberStages = stageByEntity
        this.memberRead = {
          listId,
          search,
          sort,
          limit: pagination.limit,
          page: pagination.page,
          loaded: stageByEntity.size,
          dropped,
        }
      })
    } catch (error) {
      logError(`List members read failed`, error)
      this.failMemberRead(requestId, error)
    }
  }

  // After a member read, the next page is the next page of members. `loaded` counts member rows rather
  // than contacts, because a member whose contact is gone has a row but no hit, and `hits.length` would
  // never reach `total`.
  async fetchNextPage() {
    const read = this.memberRead
    if (!read) return super.fetchNextPage()
    if (!this.list || this.listLoading || read.loaded >= this.list.total + read.dropped) return
    const page = read.page + 1
    runInAction(() => {
      this.listLoading = true
      this.listError = undefined
    })
    const requestId = ++this.listRequestId
    try {
      const next = await readListPage({
        listId: read.listId,
        search: read.search,
        sort: read.sort,
        page,
        limit: read.limit,
      })
      const { kept, dropped } = withoutRemovedMembers(read.listId, next.contacts)
      runInAction(() => {
        if (requestId !== this.listRequestId || !this.list) return
        this.listLoading = false
        this.list = {
          ...this.list,
          hits: [...this.list.hits, ...(kept as unknown as ContactType[])],
          total: next.total - read.dropped - dropped,
          page,
        }
        this.memberStages = new Map([...this.memberStages, ...next.stageByEntity])
        // Only advance the cursor once the page is in, so a failed read retries the same page.
        this.memberRead = {
          ...read,
          page,
          loaded: read.loaded + next.stageByEntity.size,
          dropped: read.dropped + dropped,
        }
      })
    } catch (error) {
      logError(`List members read failed`, error)
      this.failMemberRead(requestId, error)
    }
  }

  /** Record a stage change the cell just wrote, so a remounted row shows it before the next member read. */
  setMemberStage(entityId: string, stageId: string) {
    runInAction(() => {
      this.memberStages = new Map(this.memberStages).set(entityId, stageId)
    })
  }

  reset() {
    runInAction(() => {
      this.memberRead = undefined
      this.memberStages = new Map()
    })
    super.reset()
  }

  private failMemberRead(requestId: number, error: unknown) {
    runInAction(() => {
      if (requestId !== this.listRequestId) return
      this.listLoading = false
      this.listError = error instanceof Error ? error.message : String(error)
      notifyError(this.listError)
    })
  }

  /** Track a contact as pending for a pipeline (optimistic, pre-ES indexing). */
  addPendingPipelineContact(pipelineId: string, contact: ContactJson) {
    const existing = this.pendingPipelineContacts.get(pipelineId) ?? []
    if (!existing.some((c) => c.id === contact.id)) {
      this.pendingPipelineContacts.set(pipelineId, [...existing, contact])
    }
  }

  /** Clear pending contacts for a pipeline (call after ES has caught up). */
  clearPendingPipelineContacts(pipelineId: string) {
    this.pendingPipelineContacts.delete(pipelineId)
  }

  async mergeContacts(leftId: string, rightId: string) {
    const result = await this.client.mutation(MERGE_CONTACTS, { leftId, rightId }).toPromise()
    return result
  }

  async recalculateContact(id: string) {
    const result = await this.client.mutation(RECALCULATE_CONTACT, { id }).toPromise()
    return result
  }

  /** Append tags to a contact (server-side union). Returns the updated contact. */
  async addTags(id: string, tags: string[]) {
    const result = await this.client.mutation(ADD_TAGS_MUTATION, { id, tags }).toPromise()
    const updated = (result.data as { addTags?: ContactType } | undefined)?.addTags
    if (!result.error && updated) runInAction(() => this.intern(updated))
    return result
  }

  /** Add a contact to a pipeline stage. Returns the updated contact. */
  async addToPipeline(id: string, pipeline: { id: string; stage: string }) {
    const result = await this.client.mutation(ADD_TO_PIPELINE_MUTATION, { id, pipeline }).toPromise()
    const updated = (result.data as { addToPipeline?: ContactType } | undefined)?.addToPipeline
    if (!result.error && updated) runInAction(() => this.intern(updated))
    return result
  }

  /**
   * Merge a new tag list into the shared instance for `id`. The drawer tag editor runs its
   * own addTags/removeTags mutation (useUpdateTags), which returns only `{ tags }`. Interning
   * that partial update keeps list.hits[i] (the table row) and current (the drawer) the one
   * shared object, so the table reflects the tag change in place — no list refetch, no lag.
   */
  mergeTags(id: string, tags: string[]) {
    runInAction(() => this.intern({ id, tags } as ContactType))
  }
}
